import { CommonModule } from '@angular/common';
import { Component, Input, CUSTOM_ELEMENTS_SCHEMA, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { ButtonIconProps } from '@yooga-tecnologia/mantra/loader';

import { MenuChoiceDto, MenuItemDto } from '../../../../services/menu/dto/api-response-dto';
import { SelectProductChoicesComponent } from '../select-product-choices/select-product-choices.component';
import { CurrencyPipe } from '../../../../pipes/currency.pipe';
import { CartService, MenuItemModalData } from '../../../../domain/services/cart.service';
import { Table } from '../../../../domain/models/table';
import { Subscription } from 'rxjs';
import { TableService } from '../../../../domain/services/table.service';
import { getAutoScrollTarget } from './auto-scroll-target.util';

@Component({
  selector: 'app-product-item-with-choices-modal',
  standalone: true,
  imports: [
    CommonModule,
    CurrencyPipe,
    // AppBarComponent,
    SelectProductChoicesComponent,
  ],
  templateUrl: './product-modal-choices.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ProductModalChoicesComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() product!: MenuItemDto;
  tableSubscription: Subscription;
  readonly modalId: string = 'product-modal-with-choices';

  showScrolledHeader: boolean = false;
  dialogBody: HTMLElement | null = null;
  private scrollListener: (() => void) | null = null;
  isOpened: boolean = false;
  selectedChoices: MenuChoiceDto[] = [];
  productData!: MenuItemModalData;
  quantity: number;
  productTotal: number = 0;
  moduloMaiorValorComplemento: boolean = false;
  isEditMode: boolean = false;
  additionalInfo: string = '';
  moduleBot: boolean = false;
  backButtonProps: ButtonIconProps = {
    icon: 'caret-down',
    color: 'neutral',
    variant: 'regular',
    size: 'medium',
  };

  constructor(
    private cartService: CartService,
    private ngxSmartModalService: NgxSmartModalService,
    private cdr: ChangeDetectorRef,
    private tableService: TableService
  ) {}

  ngOnInit(): void {
    this.productData = this.ngxSmartModalService.getModal(this.modalId)?.getData() as MenuItemModalData;
    this.quantity = this.productData?.quantity || 1;
    this.productTotal = this.product.promotional_price ? this.product.promotional_price : this.product.price;

    // Check if in edit mode
    this.isEditMode = !!this.productData?.cartItemId;
    this.tableSubscription = this.tableService.$openedTable.subscribe(
      (table: Table | null) => {
        if (table) {
          this.isOpened = table.installation.isOpened;
          this.moduleBot = table.moduleBot || false;
          this.moduloMaiorValorComplemento = table.moduloMaiorValorComplemento || false;
        }
      }
    );
    // Pre-populate choices if editing
    if (this.isEditMode && this.productData?.choices) {
      this.selectedChoices = [...this.productData.choices];
      this.calculateProductTotal();
    }

    // Pre-populate additional info if editing
    if (this.isEditMode && this.productData?.additional_info) {
      this.additionalInfo = this.productData.additional_info;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.setupScrollListener();
      this.lockBodyScroll();
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.dialogBody && this.scrollListener) {
      this.dialogBody.removeEventListener('scroll', this.scrollListener);
    }

    this.unlockBodyScroll();
    this.ngxSmartModalService.resetModalData(this.modalId);
  }

  /**
   * Lock body scroll when modal is open (mobile fix)
   */
  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${this.cartService.savedScrollPosition}px`;
  }

  /**
   * Unlock body scroll when modal is closed (mobile fix)
   */
  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';

    window.scrollTo(0, this.cartService.savedScrollPosition);
  }

  closeDialog(): void {
    this.ngxSmartModalService.close(this.modalId);
  }

  onQuantityChange(event: any): void {
    if (event.detail.value > 99) {
      this.quantity = 99;
      return;
    }

    this.quantity = event.detail.value;
  }

  handleAddToCart(): void {
    if (this.isEditMode && this.productData?.cartItemId) {
      // Update existing cart item
      this.cartService.updateCartItem(
        this.productData.cartItemId,
        this.quantity,
        this.selectedChoices,
        this.productTotal,
        this.additionalInfo
      );
    } else {
      // Add new item to cart

      this.cartService.addItemToCart(this.product, this.quantity, this.selectedChoices, this.productTotal, this.additionalInfo);
    }
  }

  /**
   * Check if add to cart button should be disabled
   * Returns true if any required choice doesn't have minimum options selected
   */
  isAddToCartDisabled(): boolean {
    if (!this.product.choices) return false;

    return this.product.choices.some((choice: any) => {
      if (choice.required !== 1) return false;

      // Count selected options for this choice
      const selectedOptionsCount = this.selectedChoices
        .filter((opt: any) => opt.choiceGroupId === choice.id)
        .reduce((sum: number, opt: any) => sum + (opt.quantity || 0), 0);

      return selectedOptionsCount < choice.min;
    });
  }

  /**
   * Handle the selection of Product Additional Choice
   * Manages both radio (single choice) and multiple choice options
   * @param event EventEmitter<any>
   * @returns void
   */
  handleSelectChoice(event: any): void {
    const prevTotal = this.getGroupSelectedTotal(event.choiceGroupId);

    // Handle radio button groups (single choice) - only remove previous if max === 1
    if (event.choiceGroupId && event.isSingleChoice) {
      this.selectedChoices = this.selectedChoices.filter(
        (choice: any) => choice.choiceGroupId !== event.choiceGroupId
      );
    }

    // If quantity is 0, remove the choice from the array
    if (event.quantity === 0) {
      this.selectedChoices = this.selectedChoices.filter(
        (choice: any) => choice.id !== event.id
      );
    } else {
      // Check if option already exists
      const existingChoiceIndex = this.selectedChoices.findIndex(
        (choice: any) => choice.id === event.id
      );
      if (existingChoiceIndex >= 0) {
        // Update existing choice
        event.quantity = Number(event.quantity)
        this.selectedChoices[existingChoiceIndex] = { ...event };
      } else {
        // Add new choice
        this.selectedChoices.push(event);
      }
    }

    this.calculateProductTotal();

    const newTotal = this.getGroupSelectedTotal(event.choiceGroupId);
    this.autoScrollAfterSelection(event.choiceGroupId, event.choiceMax, prevTotal, newTotal);
  }

  /**
   * Sum the selected quantity for a given choice group
   */
  private getGroupSelectedTotal(groupId: any): number {
    return this.selectedChoices
      .filter((choice: any) => choice.choiceGroupId === groupId)
      .reduce((sum: number, choice: any) => sum + (choice.quantity || 0), 0);
  }

  /**
   * When a group just reached its max, scroll to the next group;
   * if it was the last group, scroll to the end to reveal the add button.
   */
  private autoScrollAfterSelection(groupId: any, max: number, prevTotal: number, newTotal: number): void {
    const target = getAutoScrollTarget(this.product.choices || [], groupId, max, prevTotal, newTotal);
    if (!target) return;

    setTimeout(() => {
      if (target.kind === 'next') {
        document.getElementById('choice-group-' + target.id)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const footer = document.querySelector('.dialog-footer') as HTMLElement | null;
      if (footer) {
        footer.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } else if (this.dialogBody) {
        this.dialogBody.scrollTo({ top: this.dialogBody.scrollHeight, behavior: 'smooth' });
      }
    }, 100);
  }

  /**
   * Calculate product total based on base price + selected options
   * Applies use_greater_option_price logic when applicable
   */
  private calculateProductTotal(): void {
    // Start with base product price
    let total = this.product.promotional_price ? this.product.promotional_price : this.product.price;

    // Group selected choices by choiceGroupId, filtering out choices with quantity 0
    const groupedChoices = this.selectedChoices
      .filter((choice: any) => choice.quantity > 0)
      .reduce((acc: any, choice: any) => {
        const groupId = choice.choiceGroupId;
        if (!acc[groupId]) {
          acc[groupId] = [];
        }
        acc[groupId].push(choice);
        return acc;
      }, {});

    // Calculate price for each group
    Object.keys(groupedChoices).forEach((groupId: string) => {
      const groupChoices = groupedChoices[groupId];
      const firstChoice = groupChoices[0];

      // Check if this group should use greater option price logic
      const shouldUseGreaterPrice =
        firstChoice.use_greater_option_price == true &&
        firstChoice.choiceMax > 1 &&
        this.moduloMaiorValorComplemento;
      if (shouldUseGreaterPrice) {

        const maxPrice = Math.max(...groupChoices.map((choice: any) => choice.originalPrice || choice.price || 0));

        const totalQuantity = groupChoices.reduce((sum: number, choice: any) =>
          sum + (choice.quantity || 1), 0
        );
        total += maxPrice * totalQuantity;

        groupChoices.forEach((choice: any) => {
          choice.total = maxPrice;
        });
      } else {
        groupChoices.forEach((choice: any) => {
          const optionPrice = choice.price || 0;
          const optionQty = choice.quantity || 1;
          total += optionPrice * optionQty;
        });
      }
    });

    this.productTotal = total;
  }

  onObservationChange(event: any): void {
    this.additionalInfo = event.target.value;
  }

  private setupScrollListener(): void {
    // Try multiple selectors to find the correct scrollable element
    this.dialogBody = document.querySelector('.nsm-dialog-animation-element.nsm-dialog-open') as HTMLElement;

    if (!this.dialogBody) {
      this.dialogBody = document.querySelector('.dialog-container') as HTMLElement;
    }

    if (!this.dialogBody) {
      this.dialogBody = document.querySelector('.dialog-full-height') as HTMLElement;
    }

    if (this.dialogBody) {
      this.scrollListener = () => {
        const scrollY = this.dialogBody!.scrollTop;
        const shouldShowHeader = scrollY > 200;

        if (this.showScrolledHeader !== shouldShowHeader) {
          this.showScrolledHeader = shouldShowHeader;
          this.cdr.detectChanges();
        }
      };

      this.dialogBody.addEventListener('scroll', this.scrollListener, { passive: true });
    }
  }
}